import { isFetchError } from "fetcher"
import {
  createXmppClient,
  type XmppClient,
  type XmppClientCallbacks,
} from "../xmpp/client"
import { buildUserJid, type Jid } from "../xmpp/jid"
import { auth } from "auth"
import { CHAT_WS_URL } from "../../env"
import * as chatApi from "../api"
import { backoffDelay } from "../backoff"
import { reportChatError } from "../reportChatError"
import type { ChatState } from "./store"

/**
 * How many consecutive `/auth` 401s before giving up. A rejected platform JWT
 * is a config problem or a dead Engage session, not an outage — backoff will
 * not cure it, so do not poll forever.
 */
const MAX_AUTH_REJECTS = 3

/** A 401 from `POST /auth`: Costanza rejected the platform JWT itself. */
const isAuthReject = (error: unknown) =>
  isFetchError(error) && error.status === 401

export type ReadyChatSession = ChatSession & {
  client: XmppClient
  token: string
  selfJid: Jid
}

export type SessionCallbacks = XmppClientCallbacks &
  Pick<ChatState, "reset" | "setConfig"> & {
    /**
     * Authenticated, before the socket opens — the seam for filling the store
     * from the HTTP API. Awaited, so a failure fails the boot and retries with
     * it rather than leaving a connected session with an empty list.
     *
     * A callback because the session must not reach the store directly: the
     * actions that write it take the session as an argument.
     */
    onAuthenticated: (token: string) => Promise<void>
  }

export class ChatSession {
  /**
   * Non-null while started; aborting it cancels the run's in-flight work.
   * stop() aborts and nulls it together, so non-null always means live.
   */
  private ctrl?: AbortController

  /** Set while a retry is pending — one at a time. */
  private timer?: ReturnType<typeof setTimeout>

  /** Attempts since the last live session — drives the backoff. */
  private attempt: number = 0

  /** Consecutive `/auth` 401s — see MAX_AUTH_REJECTS. */
  private authRejects: number = 0

  public client?: XmppClient
  public token?: string
  public selfJid?: Jid

  constructor(private readonly callbacks: SessionCallbacks) {}

  public isReady(): this is ReadyChatSession {
    return !!this.client && !!this.token && !!this.selfJid
  }

  /**
   * Idempotent: a second start would orphan the first AbortController, so
   * stop() could no longer cancel that run's fetches and its boot would go on
   * to open a second socket. `<ChatProvider>` relies on this — React 18
   * StrictMode double-invokes the mount effect in dev.
   */
  start() {
    if (this.ctrl) return

    this.ctrl = new AbortController()
    void this.#boot()
  }

  stop() {
    this.ctrl?.abort()
    delete this.ctrl
    if (this.timer) clearTimeout(this.timer)
    delete this.timer
    this.attempt = 0
    this.authRejects = 0
    this.client?.disconnect()
    delete this.client
    delete this.token
    delete this.selfJid
    this.callbacks.reset()
  }

  reboot(reason: string) {
    if (!this.ctrl) return // stopped — stay down
    if (this.timer) return // one pending reboot at a time

    const delay = backoffDelay(this.attempt++)
    console.warn(
      `[chat] ${reason} — reconnecting in ${Math.round(delay)}ms (attempt ${this.attempt})`
    )
    this.callbacks.onConnStateChange("reconnecting")

    this.timer = setTimeout(() => {
      delete this.timer
      this.disconnect() // never leave two live clients behind

      void this.#boot()
    }, delay)
  }

  markLive() {
    this.attempt = 0
  }

  /**
   * A mid-session 401 means the session token aged out while the socket stayed
   * up — same cure as a rejected token: retire the client, re-auth, reconnect.
   * Any other failure is left to the caller, which for a foreground call
   * (`groups.ts` rethrows to a modal/react-query) shows it to the user itself —
   * reporting here too would double-count expected input errors. Background
   * callers use `recover`, which does report.
   *
   * An arrow property, not a method: callers pass it as a value
   * (`.catch(session.refreshOn401)`), which a method would unbind.
   */
  refreshOn401 = (error: unknown) => {
    if (!isFetchError(error) || error.status !== 401) return

    this.disconnect()
    this.reboot("session token expired mid-session")
  }

  /**
   * For fire-and-forget background calls (read receipts, room naming) that have
   * no caller to surface a failure: reboot on a 401 like `refreshOn401`, but
   * report anything else rather than swallow it — a background call that keeps
   * failing is a signal, and nothing else would say so.
   */
  recover = (error: unknown) => {
    if (isFetchError(error) && error.status === 401) {
      this.disconnect()
      this.reboot("session token expired mid-session")
      return
    }

    reportChatError("background chat request failed", error)
  }

  /**
   * Bring chat back after it gave up — the seam for a "reconnect" button on the
   * terminal `failed` state. Clears the strike counts so the fresh run starts
   * from scratch, then boots immediately rather than waiting out a backoff the
   * user has just overridden by asking.
   */
  reconnect() {
    if (!this.ctrl) return // stopped — a remount will start it

    if (this.timer) clearTimeout(this.timer)
    delete this.timer
    this.attempt = 0
    this.authRejects = 0
    this.disconnect()
    this.callbacks.onConnStateChange("connecting")
    void this.#boot()
  }

  disconnect() {
    void this.client?.disconnect()
    delete this.client
  }

  async #boot() {
    const ctrl = this.ctrl
    if (!ctrl) return
    if (!CHAT_WS_URL) return // chat unconfigured

    try {
      const { accessToken, companyNameKey } = await auth.getActiveSession()
      if (ctrl.signal.aborted || !accessToken) return

      const { sessionToken, handle, retractionWindowHours } =
        await chatApi.postAuth(accessToken, ctrl.signal)

      this.authRejects = 0
      this.token = sessionToken
      this.selfJid = buildUserJid(handle, companyNameKey)

      this.callbacks.setConfig({
        selfJid: this.selfJid,
        retractionWindowHours,
      })
      await this.callbacks.onAuthenticated(this.token)
      if (ctrl.signal.aborted) return

      this.client = createXmppClient(
        {
          jid: this.selfJid,
          sessionToken: this.token,
          wsUrl: CHAT_WS_URL,
        },
        this.callbacks
      )

      await this.client.connect()
    } catch (error) {
      // Teardown aborts the in-flight fetch; that rejection is not an error.
      if (ctrl.signal.aborted) return
      // Costanza down or misconfigured.
      if (isAuthReject(error) && ++this.authRejects >= MAX_AUTH_REJECTS)
        return this.#die()

      this.#logBootError(error)
      this.reboot("session start failed")
    }
  }

  #logBootError(error: unknown) {
    if (isFetchError(error)) {
      reportChatError(
        `failed to start session — ${error.status} ${error.url}: ${error.json ?? error.text ?? error.message}`,
        error
      )
    } else {
      reportChatError("failed to start session", error)
    }
  }

  #die() {
    reportChatError(
      `${MAX_AUTH_REJECTS} consecutive auth rejections — giving up (Costanza misconfigured, or the platform session is dead)`
    )

    // Terminal: backoff will not cure a dead platform session, so stop here and
    // let the UI offer a manual reconnect instead of a silent disconnected state.
    this.callbacks.onConnStateChange("failed")
  }
}
